import type { DeliveryType, Nullable } from '@libs/core';
import { HttpStatus } from '@libs/core';
import type {
    BaseError,
    CartErrorResponse,
    CheckoutPaymentErrorResponse,
    InventoryResponseError,
    LineItemError,
    PaymentResponseError,
} from '@libs/common-node';
import type { CartValidationErrorDetails } from '../../types';
import { isCartClientErrorResponse } from './error/cartBadRequestError';

export const unexpectedError = {
    errorCode: 'cart.checkout.unexpectederrorbanner',
    message: 'An unexpected error occurred. Please try again later.',
};

export const parseErrorResponse = (
    cartError?: Nullable<CartErrorResponse> | (Response & { parsedResponse: CartErrorResponse }),
    attemptedFulfillmentMethod?: DeliveryType
): CartValidationErrorDetails[] => {
    // "400 - Bad request" case with 'Validation Error'
    if (!cartError || typeof cartError === 'string') {
        return [unexpectedError];
    }

    const errorResponse = 'parsedResponse' in cartError ? cartError.parsedResponse : cartError;

    // Handle 5xx errors explicitly
    if (
        errorResponse?.status >= HttpStatus.INTERNAL_SERVER_ERROR &&
        errorResponse?.status < HttpStatus.NETWORK_AUTHENTICATION_REQUIRED
    ) {
        return [unexpectedError];
    }

    // 4xx - Client errors based on https://petsmart.atlassian.net/wiki/spaces/DPT1/pages/770050236/Cart+API+Definition+V3#Error-response
    if (isCartClientErrorResponse(errorResponse)) {
        const { errors } = errorResponse;

        return (
            errors?.map(error => ({
                errorCode: error.errorCode || unexpectedError.errorCode,
                message: error.message || unexpectedError.message,
            })) || [unexpectedError]
        );
    }

    // 422 Cart validation case: https://petsmart.atlassian.net/wiki/spaces/DPT1/pages/770050236/Cart+API+Definition+V3#Validation-response
    const errors = errorResponse?.errors;

    const cartErrors =
        errors?.cartErrors?.map((cartErr: BaseError) => ({
            errorCode: cartErr.errorCode,
            message: cartErr.message,
        })) || [];

    const responseInventoryErrors = Array.isArray(errorResponse?.inventory_errors)
        ? errorResponse?.inventory_errors
        : [errorResponse?.inventory_errors];

    const responsePaymentErrors = Array.isArray(errorResponse?.payment_errors)
        ? errorResponse?.payment_errors
        : [errorResponse?.payment_errors];

    // Check for inventory_errors in the errorResponse
    const inventoryErrors =
        (responseInventoryErrors as InventoryResponseError[])?.filter(Boolean).map((error: InventoryResponseError) => ({
            lineItemId: error.id,
            errorCode: error.errorCode,
            message: error.message,
            productName: error.productName,
            available: error.available,
            isAttemptedFMTransition: Boolean(attemptedFulfillmentMethod),
        })) || [];

    const paymentErrors =
        (responsePaymentErrors as PaymentResponseError[])?.filter(Boolean).map((error: PaymentResponseError) => ({
            errorCode: error.errorCode ?? unexpectedError.errorCode,
            message: error.errorMessage ?? unexpectedError.message,
        })) || [];

    const { lineItemErrors = [], lineErrors = [] } = errors || {};

    const parsedLineErrors =
        [...lineItemErrors, ...lineErrors].flatMap(
            lineErr =>
                lineErr.lineItemErrors?.map((lineItemErr: LineItemError) => ({
                    lineItemId: lineErr.lineItemId,
                    productName: lineErr.productName,
                    errorCode: lineItemErr.errorCode,
                    message: lineItemErr.message,
                    available: lineItemErr.available,
                })) || []
        ) || [];

    const mergedErrors = [...cartErrors, ...parsedLineErrors, ...inventoryErrors, ...paymentErrors];

    return mergedErrors.length > 0 ? mergedErrors : [unexpectedError];
};

export const parseCheckoutPaymentErrorResponse = (
    checkoutPaymentError?: Nullable<CheckoutPaymentErrorResponse>
): CartValidationErrorDetails[] => checkoutPaymentError?.flatMap(err => err.paymentErrors || []) || [];
